import React from "react";
import { createMuiTheme, Theme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import { SessionSobolModel } from "../../helpers/sessionSobol";
import { UserRole } from "../../helpers/userRole";
import { Partner, PartnerNames } from "../../helpers/partner";
import { inject, observer } from "mobx-react";
import { UiFlagsStore } from "../../stores/uiFlagsStore";
import { UserStore } from "../../stores/userStore";
import "./app.scss";
import { Environment, Environments } from "../../helpers/envinonment";
import { Widget } from "../../helpers/widget";
import { Steps } from "../../helpers/step";
import Apk from "../apk/apk";
import QuestionsToOffer from "../questionsToOffer/questionsToOffer";
import OfferCustomizer from "../offerCustomizer/offerCustomizer";
import ContractData from "../contractData/contractData";
import ThankYouPage from "../thankYouPage/thankYouPage";
import FakeComponents from "./fakeComponents/fakeComponents";
import ExceededProxies from "../exceededProxies/exceededProxies";
import { RetrieveCalculationService } from "../../services/services/retrieveCalculationService";
import { Popup } from "../sharedComponents/popup/popup";
import RetrieveCalculationPopupContent from "./retrieveCalculationPopupContent/retrieveCalculationPopupContent";
import { DictionariesService } from "../../services/services/dictionariesService";
import { PolicyStore } from "../../stores/policyStore";
import AuthorizationForRenewal from "./authorizationForRenewal/authorizationForRenewal";
import { PolicyService } from "../../services/services/policyService";
import { ValidatedValue } from "../../helpers/validator";
import ReactGA from "react-ga4";

declare global {
  interface Window {
    session: SessionSobolModel;
  }
}

interface AppProps {
  uiFlagsStore?: UiFlagsStore;
  userStore?: UserStore;
  policyStore?: PolicyStore;
  retrieveCalculationService?: RetrieveCalculationService;
  dictionariesService?: DictionariesService;
  partnerName: string;
  environment: string;
  policyService?: PolicyService;
}

interface AppState {
  calculationId: string;
  contractId: string;
  renewalOfferId: string;
  loading: boolean;
  isDataLoaded: boolean;
}

@inject("uiFlagsStore", "userStore", "retrieveCalculationService", "dictionariesService", "policyStore", "policyService")
@observer
export default class App extends React.Component<AppProps, AppState> {
  private url: URL;
  private theme: Theme;

  constructor(props: AppProps) {
    super(props);
    this.url = new URL(window.location.href);
    this.theme = createMuiTheme();

    this.initApplication();
  }
  public async componentDidMount() {
    try {
      await this.fetchData();
      this.updateDictionaries();

      window.addEventListener("resize", this.handleResize);
      Widget.resize(this.props.uiFlagsStore!);

      ReactGA.event({ category: "event", action: "apkEntry" });

      this.setState({ isDataLoaded: true });
    } catch (error) {
      // @NOTE: w celach testowych jedynie - console.error("Error while fetching data:", error);
    }
  }

  private handleResize = () => {
    Widget.resize(this.props.uiFlagsStore!);
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }


  public render() {
    const { loading } = this.state;
    return (
      <ThemeProvider theme={this.theme}>
        <div id="tuz-estate-widget-app" className={`app ${this.props.uiFlagsStore!.partnerName}`}>
          {this.renderRetrieveCalculation()}
          {!loading && this.renderContent()}
        </div>
        {/* We render hidden FakeComponent at application startup, because some styles download too late */}
        <FakeComponents />
        <div className="footer" />
      </ThemeProvider>
    );
  }
  private initApplication() {
    this.setEnvironment();
    this.setPartnerName();
    this.setApiAndSourceDomain();
    this.setUserData();
    this.setThemePalette();
    this.initAppState();
    this.setActiveStep();
  }

  private setActiveStep() {
    if (this.isNotAuthorizedRenewalOfferForDirect()) {
      this.props.uiFlagsStore!.goToAuthorizationForRenewalPage();
    } else {
      this.props.uiFlagsStore!.goToNewCalculation();
    }
  }

  private isNotAuthorizedRenewalOfferForDirect(): boolean {
    const searchParams = this.url.searchParams;
    const hasRenewalParam = searchParams.get("p") || searchParams.get("c");
    const hasRenewalOfferIdParam = searchParams.get("renewalOfferId");
    return !!(this.props.uiFlagsStore!.isDirect && !hasRenewalOfferIdParam && hasRenewalParam);
  }

  private isAuthorizedRenewalOfferForDirect(hasRenewalOfferIdParam: string | null): boolean {
    return !!(this.props.uiFlagsStore!.isDirect && hasRenewalOfferIdParam);
  }

  private async retrievePolicyForDirect(renewalOfferId: string | null) {
    if (renewalOfferId) {
      const resp = await this.props.policyService!.getPolicyRenewalDataRequest(renewalOfferId);
      const policyNumber = resp?.number;
      const policySeries = resp?.series;
      const peselOrRegon = resp?.peselOrRegon;

      if (policySeries && policyNumber && peselOrRegon) {
        this.props.policyStore!.setSeries(policySeries);
        this.props.policyStore!.setNumber(policyNumber);
        this.props.policyStore!.setPeselOrRegon(peselOrRegon);

        await this.props.retrieveCalculationService!.retrieveContractForRenewalBySeriesAndNumberRequest({
          series: policySeries,
          number: new ValidatedValue<string>(policyNumber),
          peselOrRegon: new ValidatedValue<string>(peselOrRegon)
        });
      }
    }
  }

  private renderRetrieveCalculation() {
    const retrieveCalculationService = this.props.retrieveCalculationService!;
    const contractRequestStatus = this.state.contractId ?
      retrieveCalculationService.retrieveContractForRenewalByIdRequestStatus :
      retrieveCalculationService.retrieveContractForRenewalBySeriesAndNumberRequestStatus;
    const requestStatus = this.state.calculationId ? retrieveCalculationService.retrieveCalculationRequestStatus : contractRequestStatus;
    const error = requestStatus.isError ?? false;
    const pending = requestStatus.isPending ?? false;

    return (<Popup
      className="retrieve-calculation-popup"
      open={pending || error}
      displayAction={error}
      actionDisabled={pending}
      action={() => {
        this.retrieveCalculationAction(error);
      }}
      actionText={error ? "Spróbuj ponownie" : "Dalej"}
      displayCancel={error}
      cancelText={"Anuluj"}
      cancel={() => {
        requestStatus.reset();
      }}
      cancelClassName={"cancel-button"}
      dialogContent={<RetrieveCalculationPopupContent requestStatus={requestStatus} />}
    />);
  }

  private retrieveCalculationAction(error: boolean | undefined) {
    if (error && this.state.calculationId) {
      this.props.retrieveCalculationService?.retrieveCalculationRequest(this.state.calculationId);
    } else if (error && this.state.contractId) {
      this.props.retrieveCalculationService!.retrieveContractForRenewalByIdRequest(this.state.contractId);
    } else if (error && this.state.renewalOfferId) {
      const policyRenewal = this.props.policyStore!.policyRenewal;
      this.props.retrieveCalculationService!.retrieveContractForRenewalBySeriesAndNumberRequest(policyRenewal);
    } else if (error && !this.state.contractId && !this.state.calculationId && !this.state.renewalOfferId) {
      const policyRenewal = this.props.policyStore!.policyRenewal;
      this.props.retrieveCalculationService!.retrieveContractForRenewalBySeriesAndNumberRequest(policyRenewal);
    }
  }

  private setThemePalette() {
    this.theme = createMuiTheme({
      palette: {
        primary: { main: "#182f88" },
        secondary: { main: "#171c8f" }
      }
    });
  }

  private updateDictionaries() {
    this.props.dictionariesService!.refreshBanks();
    this.props.dictionariesService!.refreshPaymentMethods();
  }

  private setUserData() {
    this.props.userStore!.setUsername(window.session ? window.session.user_name : undefined);
    this.props.userStore!.setSessionId(window.session ? window.session.id : undefined);
    this.props.userStore!.setRoles(this.retrieveRoles());
  }

  private retrieveRoles() {
    const rolesFromUrl = this.url.searchParams.get("roles");

    if (Environment.checkIfTestOrLocalhostEnvironment(this.props.uiFlagsStore!) && rolesFromUrl) {
      return this.mapRoles(rolesFromUrl.split(","));
    } else if (window.session && window.session?.user_roles) {
      return this.mapRoles(window.session.user_roles);
    }
    return undefined;
  }

  private mapRoles(userRoles: string[]): UserRole[] | undefined {
    return userRoles
      .map((role) => Number(role))
      .filter((role) => !isNaN(role))
      .map((role) => UserRole.fromCode(role))
      .filter((role) => role)
      .map((role) => role as UserRole);
  }

  private setEnvironment() {
    const environment = Environment.checkIsCorrectEnvironment(this.props.environment) ?
      this.props.environment as Environments :
      Environments.default;

    this.props.uiFlagsStore!.setEnvironment(environment);
    this.initializeGA(environment);
  }

  private setPartnerName() {
    const partnerNameFromUrl = this.url.searchParams.get("partnerName");
    let partnerName = this.props.partnerName;

    if (Environment.checkIfTestOrLocalhostEnvironment(this.props.uiFlagsStore!) && partnerNameFromUrl) {
      partnerName = partnerNameFromUrl;
    }

    partnerName = Partner.checkIsCorrectPartnerName(partnerName) ?
      partnerName as PartnerNames :
      PartnerNames.default;

    this.props.uiFlagsStore!.setPartnerName(partnerName as PartnerNames);
  }

  private setApiAndSourceDomain() {
    let sourceDomain = null;

    if (Environment.checkIfLocalhostEnvironment(this.props.uiFlagsStore!)) {
      sourceDomain = "https://domieszkanie.int-test.tuz.pl/";
    } else {
      const embeddingScript = document.getElementById("tuz_estate_widget_embedding_script");
      sourceDomain = embeddingScript?.getAttribute("src") || null;

      if (sourceDomain) {
        sourceDomain = sourceDomain.replace(/embeddingScript.js.*/i, "");
      }
    }

    this.props.uiFlagsStore!.setApiDomain(`${sourceDomain}api/`);
    this.props.uiFlagsStore!.setSourceDomain(sourceDomain || "");
  }

  private getHashParam(param: "calculationId" | "contractId"): string | null {
    if (this.props.uiFlagsStore!.isDirect) {
      return null;
    }
    let hash = this.url.hash;

    if (hash) {
      hash = hash.substring(1);
    }

    const queryString = require("query-string");
    const parsed = queryString.parse(hash);

    return parsed[param];
  }

  private initAppState() {
    this.state = {
      calculationId: "",
      contractId: "",
      renewalOfferId: "",
      loading: true,
      isDataLoaded: false
    };
  }

  private async fetchData() {
    const calculationId = this.getHashParam("calculationId");
    const contractId = this.getHashParam("contractId");
    const renewalOfferId = this.url.searchParams.get("renewalOfferId");

    if (calculationId) {
      await this.retrieveCalculationById(calculationId);
    } else if (contractId) {
      await this.retrieveContractById(contractId);
    } else if (this.isAuthorizedRenewalOfferForDirect(renewalOfferId)) {
      await this.retrievePolicyForDirect(renewalOfferId);
    }

    this.setState({
      calculationId: calculationId ?? "",
      contractId: contractId ?? "",
      renewalOfferId: renewalOfferId ?? "",
      loading: false
    });
  }

  private async retrieveCalculationById(calculationId: string) {
    await this.props.retrieveCalculationService!.retrieveCalculationRequest(calculationId);
  }

  private async retrieveContractById(contractId: string) {
    await this.props.retrieveCalculationService!.retrieveContractForRenewalByIdRequest(contractId);
  }
  private initializeGA(environment: Environments) {
    const gaMeasurementId = environment === Environments.prod
        ? "G-C3WY2TGFR"
        : "G-33Z0TL1BQC";

    ReactGA.initialize(gaMeasurementId);
  }
  private renderContent() {
    const steps = {
      [Steps.apk]: <Apk />,
      [Steps.questionsToOffer]: <QuestionsToOffer />,
      [Steps.offerCustomizer]: <OfferCustomizer />,
      [Steps.contractData]: <ContractData />,
      [Steps.thankYouPage]: <ThankYouPage />,
      [Steps.proxiesExceeded]: <ExceededProxies />,
      [Steps.authorizationForRenewal]: <AuthorizationForRenewal />
    };

    return steps[this.props.uiFlagsStore!.activeStep];
  }
}
